<script lang="ts" setup>
import { useAnalytics } from "@/composables/useAnalytics";
interface Props {
  to?: string | object;
  text?: string;
  variant?: "primary" | "secondary" | "outline" | "glass" | "white" | "black";
  size?: "sm" | "md" | "lg";
  icon?: string;
}

const props = withDefaults(defineProps<Props>(), {
  variant: "primary",
  size: "md",
});

const emit = defineEmits(["click"]);
const { trackCtaClick } = useAnalytics();

const handleClick = (event: MouseEvent) => {
  trackCtaClick(props.text || "unnamed_button", props.to ? "link" : "action");
  emit("click", event);
};

const variantClasses = {
  primary: "bg-primary text-white before:bg-primary shadow-primary/25",
  secondary: "bg-secondary text-white before:bg-secondary shadow-secondary/25",
  outline:
    "bg-transparent text-primary border-2 border-primary before:bg-primary/5",
  glass:
    "bg-white/10 backdrop-blur-md text-white border border-white/20 before:bg-white/20",
  white: "bg-white text-primary before:bg-gray-100 shadow-white/10",
  black: "bg-gray-900 text-white before:bg-black shadow-black/10",
};

const sizeClasses = {
  sm: "h-9 px-4 text-sm",
  md: "h-11 px-6 text-base",
  lg: "h-14 px-10 text-lg",
};
</script>

<template>
  <NuxtLink
    v-if="to"
    :to="to"
    class="inline-block group"
    v-bind="$attrs"
    @click="handleClick"
  >
    <button
      type="button"
      :class="[
        'relative inline-flex items-center justify-center font-bold transition-all duration-300 rounded-full cursor-pointer w-full overflow-hidden hover:scale-105 active:scale-95 shadow-lg',
        variantClasses[variant],
        sizeClasses[size],
      ]"
    >
      <!-- Base Background for internal structure if needed -->
      <span
        class="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300"
        :class="variant === 'outline' ? '' : 'bg-white/10'"
      ></span>

      <span class="relative flex items-center gap-2 z-10">
        <Icon v-if="icon" :name="icon" class="w-5 h-5" />
        <slot>{{ text }}</slot>
      </span>
    </button>
  </NuxtLink>

  <button
    v-else
    v-bind="$attrs"
    type="button"
    @click="handleClick"
    :class="[
      'group relative inline-flex items-center justify-center font-bold transition-all duration-300 rounded-full cursor-pointer overflow-hidden hover:scale-105 active:scale-95 shadow-lg',
      variantClasses[variant],
      sizeClasses[size],
    ]"
  >
    <span
      class="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300"
      :class="variant === 'outline' ? '' : 'bg-white/10'"
    ></span>

    <span class="relative flex items-center gap-2 z-10">
      <Icon v-if="icon" :name="icon" class="w-5 h-5" />
      <slot>{{ text }}</slot>
    </span>
  </button>
</template>
